import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import SandboxBadge from "@/components/SandboxBadge";
import FlexauraVerifiedBadge, { VerificationDisclaimer } from "@/components/FlexauraVerifiedBadge";
import { DEFAULT_VERIFICATION } from "@/lib/merchant-data";
import ProviderLogoPills from "@/components/ProviderLogoPills";
import CompareAllBar from "@/components/CompareAllBar";
import DataFreshnessNotice from "@/components/DataFreshnessNotice";
import ReportListingModal from "@/components/ReportListingModal";

// Import assets
import heroHealthcareImg from "@/assets/hero-healthcare.jpg";

type TreatmentPackage = {
  id: string;
  title: string;           // Procedure name
  description: string;     // Notes / typical inclusions
  price: number;           // Estimated total (AED)
  image: string;
  duration: string;        // Care timeline
  category: string;        // Specialty
  facility?: string;
};

const packages: TreatmentPackage[] = [
  // Maternity & Women's Health
  {
    id: "hc-maternity-delivery",
    title: "Maternity Delivery Package",
    description: "Normal delivery package (room, obstetrician fees, labs). Estimate; final bill varies by facility.",
    price: 18000,
    image: "/healthcare-maternity.jpg",
    duration: "3–5 days inpatient",
    category: "Maternity",
    facility: "Multiple hospitals"
  },
  {
    id: "hc-csection",
    title: "Cesarean Section (C-Section)",
    description: "Elective/medical. Estimate excludes NICU. Financing can stage pre-admission deposit + post-discharge.",
    price: 32000,
    image: "/healthcare-csection.jpg",
    duration: "3–5 days inpatient",
    category: "Maternity",
    facility: "Private hospitals"
  },
  {
    id: "hc-ivf-cycle",
    title: "IVF Cycle",
    description: "Includes meds + procedures for one cycle (varies by clinic).",
    price: 28000,
    image: "/healthcare-ivf.jpg",
    duration: "4–6 weeks",
    category: "Fertility",
    facility: "Fertility clinics"
  },
  {
    id: "hc-prenatal-care",
    title: "Complete Prenatal Care Package",
    description: "Full pregnancy monitoring, scans, tests, and consultations.",
    price: 15000,
    image: "/healthcare-maternity.jpg",
    duration: "9 months",
    category: "Maternity",
    facility: "Multiple clinics"
  },
  {
    id: "hc-hysterectomy",
    title: "Hysterectomy (Laparoscopic)",
    description: "Minimally invasive procedure including surgeon and facility fees.",
    price: 35000,
    image: "/healthcare-csection.jpg",
    duration: "2–3 days inpatient",
    category: "Gynecology",
    facility: "Private hospitals"
  },

  // Orthopedics & Sports Medicine
  {
    id: "hc-knee-arthroscopy",
    title: "Knee Arthroscopy",
    description: "Orthopedic procedure; estimate includes surgeon + facility.",
    price: 24000,
    image: "/healthcare-knee.jpg",
    duration: "Day surgery",
    category: "Orthopedics"
  },
  {
    id: "hc-hip-replacement",
    title: "Total Hip Replacement",
    description: "Complete hip joint replacement with titanium implant.",
    price: 85000,
    image: "/healthcare-knee.jpg",
    duration: "4–6 days inpatient",
    category: "Orthopedics",
    facility: "Orthopedic centers"
  },
  {
    id: "hc-shoulder-surgery",
    title: "Shoulder Rotator Cuff Repair",
    description: "Arthroscopic repair of torn rotator cuff with rehabilitation.",
    price: 28000,
    image: "/healthcare-knee.jpg",
    duration: "Day surgery + physio",
    category: "Orthopedics"
  },
  {
    id: "hc-spine-fusion",
    title: "Lumbar Spine Fusion",
    description: "L4-L5 fusion surgery for chronic back pain relief.",
    price: 95000,
    image: "/healthcare-knee.jpg",
    duration: "3–5 days inpatient",
    category: "Orthopedics",
    facility: "Spine centers"
  },
  {
    id: "hc-acl-reconstruction",
    title: "ACL Reconstruction Surgery",
    description: "Complete anterior cruciate ligament reconstruction.",
    price: 35000,
    image: "/healthcare-knee.jpg",
    duration: "Day surgery",
    category: "Sports Medicine"
  },

  // Ophthalmology & ENT
  {
    id: "hc-lasik",
    title: "LASIK (Both Eyes)",
    description: "Refractive surgery; typically outpatient.",
    price: 9000,
    image: "/healthcare-lasik.jpg",
    duration: "Same day",
    category: "Ophthalmology"
  },
  {
    id: "hc-cataract-surgery",
    title: "Cataract Surgery (Both Eyes)",
    description: "Phacoemulsification with premium IOL lenses.",
    price: 18000,
    image: "/healthcare-lasik.jpg",
    duration: "Outpatient procedure",
    category: "Ophthalmology"
  },
  {
    id: "hc-retinal-detachment",
    title: "Retinal Detachment Repair",
    description: "Vitrectomy procedure for retinal reattachment.",
    price: 25000,
    image: "/healthcare-lasik.jpg",
    duration: "Day surgery",
    category: "Ophthalmology"
  },
  {
    id: "hc-sinus-surgery",
    title: "Endoscopic Sinus Surgery",
    description: "Functional endoscopic sinus surgery for chronic sinusitis.",
    price: 22000,
    image: "/healthcare-dental.jpg",
    duration: "Day surgery",
    category: "ENT"
  },
  {
    id: "hc-tonsillectomy",
    title: "Tonsillectomy (Adult)",
    description: "Surgical removal of tonsils and adenoids.",
    price: 12000,
    image: "/healthcare-dental.jpg",
    duration: "Day surgery",
    category: "ENT"
  },

  // Dental & Oral Surgery
  {
    id: "hc-dental-implants",
    title: "Dental Implants (2 units)",
    description: "Implant + crown. May require bone graft (extra).",
    price: 16000,
    image: "/healthcare-dental.jpg",
    duration: "2–3 months total",
    category: "Dentistry"
  },
  {
    id: "hc-root-canal-crowns",
    title: "Root Canal + Crown (2 teeth)",
    description: "Endodontics + crowns.",
    price: 7800,
    image: "/healthcare-root.jpg",
    duration: "2–3 weeks",
    category: "Dentistry"
  },
  {
    id: "hc-full-mouth-restoration",
    title: "Full Mouth Dental Restoration",
    description: "Complete smile makeover with veneers and crowns.",
    price: 45000,
    image: "/healthcare-dental.jpg",
    duration: "3–4 months",
    category: "Cosmetic Dentistry"
  },
  {
    id: "hc-orthodontics-braces",
    title: "Orthodontic Treatment (Braces)",
    description: "Complete orthodontic treatment with metal braces.",
    price: 18000,
    image: "/healthcare-dental.jpg",
    duration: "18–24 months",
    category: "Orthodontics"
  },
  {
    id: "hc-invisalign",
    title: "Invisalign Clear Aligners",
    description: "Invisible orthodontic treatment with clear aligners.",
    price: 28000,
    image: "/healthcare-dental.jpg",
    duration: "12–18 months",
    category: "Orthodontics"
  },
  {
    id: "hc-wisdom-teeth",
    title: "Wisdom Teeth Extraction (4 teeth)",
    description: "Surgical extraction of all four wisdom teeth.",
    price: 8500,
    image: "/healthcare-dental.jpg",
    duration: "Day surgery",
    category: "Oral Surgery"
  },

  // General & Bariatric Surgery
  {
    id: "hc-bariatric",
    title: "Bariatric Surgery",
    description: "Pre-op + surgery + early follow-up; exclusions apply.",
    price: 45000,
    image: "/healthcare-bariatric.jpg",
    duration: "2–3 days inpatient",
    category: "General Surgery"
  },
  {
    id: "hc-gallbladder",
    title: "Laparoscopic Gallbladder Surgery",
    description: "Minimally invasive gallbladder removal (cholecystectomy).",
    price: 28000,
    image: "/healthcare-bariatric.jpg",
    duration: "1–2 days inpatient",
    category: "General Surgery"
  },
  {
    id: "hc-hernia-repair",
    title: "Inguinal Hernia Repair",
    description: "Laparoscopic hernia repair with mesh reinforcement.",
    price: 18000,
    image: "/healthcare-bariatric.jpg",
    duration: "Day surgery",
    category: "General Surgery"
  },
  {
    id: "hc-appendectomy",
    title: "Laparoscopic Appendectomy",
    description: "Minimally invasive appendix removal procedure.",
    price: 22000,
    image: "/healthcare-bariatric.jpg",
    duration: "1–2 days inpatient",
    category: "General Surgery"
  },

  // Plastic & Cosmetic Surgery
  {
    id: "hc-cosmetic-rhinoplasty",
    title: "Cosmetic Rhinoplasty",
    description: "Elective. Facility + anesthesia + surgeon.",
    price: 28000,
    image: "/healthcare-rhino.jpg",
    duration: "Day surgery + follow-ups",
    category: "Plastic Surgery"
  },
  {
    id: "hc-breast-augmentation",
    title: "Breast Augmentation",
    description: "Cosmetic breast enhancement with implants.",
    price: 35000,
    image: "/healthcare-rhino.jpg",
    duration: "Day surgery",
    category: "Plastic Surgery"
  },
  {
    id: "hc-tummy-tuck",
    title: "Abdominoplasty (Tummy Tuck)",
    description: "Complete abdominal contouring and muscle repair.",
    price: 42000,
    image: "/healthcare-rhino.jpg",
    duration: "Day surgery + recovery",
    category: "Plastic Surgery"
  },
  {
    id: "hc-liposuction",
    title: "Liposuction (Multiple Areas)",
    description: "Body contouring with fat removal from 3-4 areas.",
    price: 25000,
    image: "/healthcare-rhino.jpg",
    duration: "Day surgery",
    category: "Plastic Surgery"
  },
  {
    id: "hc-facelift",
    title: "Facelift (Rhytidectomy)",
    description: "Complete facial rejuvenation surgery.",
    price: 48000,
    image: "/healthcare-rhino.jpg",
    duration: "Day surgery",
    category: "Plastic Surgery"
  },
  {
    id: "hc-hair-transplant",
    title: "Hair Transplant (FUE)",
    description: "Follicular unit extraction hair restoration (3000 grafts).",
    price: 18000,
    image: "/healthcare-rhino.jpg",
    duration: "Full day procedure",
    category: "Hair Restoration"
  },

  // Cardiology & Vascular
  {
    id: "hc-cardiac-catheter",
    title: "Cardiac Catheterization",
    description: "Diagnostic heart catheter with possible angioplasty.",
    price: 35000,
    image: "/healthcare-knee.jpg",
    duration: "1–2 days inpatient",
    category: "Cardiology"
  },
  {
    id: "hc-bypass-surgery",
    title: "Coronary Artery Bypass",
    description: "Open heart surgery with multiple vessel grafts.",
    price: 125000,
    image: "/healthcare-knee.jpg",
    duration: "5–7 days inpatient",
    category: "Cardiac Surgery",
    facility: "Cardiac centers"
  },
  {
    id: "hc-varicose-veins",
    title: "Varicose Vein Treatment",
    description: "Endovenous laser treatment for varicose veins.",
    price: 15000,
    image: "/healthcare-knee.jpg",
    duration: "Outpatient procedure",
    category: "Vascular Surgery"
  },
  {
    id: "hc-pacemaker",
    title: "Pacemaker Implantation",
    description: "Cardiac pacemaker insertion with programming.",
    price: 55000,
    image: "/healthcare-knee.jpg",
    duration: "2–3 days inpatient",
    category: "Cardiology"
  },

  // Dermatology & Aesthetics
  {
    id: "hc-skin-cancer",
    title: "Skin Cancer Removal (Mohs)",
    description: "Mohs micrographic surgery for skin cancer treatment.",
    price: 12000,
    image: "/healthcare-lasik.jpg",
    duration: "Day surgery",
    category: "Dermatology"
  },
  {
    id: "hc-laser-treatment",
    title: "Laser Skin Resurfacing",
    description: "CO2 fractional laser for skin rejuvenation.",
    price: 8500,
    image: "/healthcare-lasik.jpg",
    duration: "Outpatient procedure",
    category: "Dermatology"
  },
  {
    id: "hc-botox-fillers",
    title: "Botox & Dermal Fillers Package",
    description: "Complete facial rejuvenation with injectables.",
    price: 5500,
    image: "/healthcare-lasik.jpg",
    duration: "1 hour procedure",
    category: "Aesthetics"
  },

  // Wellness & Preventive
  {
    id: "hc-executive-checkup",
    title: "Executive Health Checkup",
    description: "Comprehensive health screening with specialist consultations.",
    price: 8000,
    image: "/healthcare-maternity.jpg",
    duration: "Full day assessment",
    category: "Preventive Care"
  },
  {
    id: "hc-cardiac-screening",
    title: "Cardiac Screening Package",
    description: "Complete heart health assessment with stress test.",
    price: 4500,
    image: "/healthcare-maternity.jpg",
    duration: "Half day",
    category: "Preventive Care"
  },
  {
    id: "hc-cancer-screening",
    title: "Cancer Screening Package",
    description: "Comprehensive cancer screening for early detection.",
    price: 6500,
    image: "/healthcare-maternity.jpg",
    duration: "2–3 visits",
    category: "Preventive Care"
  },

  // Mental Health & Wellness
  {
    id: "hc-therapy-package",
    title: "Psychotherapy Package (12 sessions)",
    description: "Individual counseling sessions with licensed therapist.",
    price: 7200,
    image: "/healthcare-maternity.jpg",
    duration: "3 months",
    category: "Mental Health"
  },
  {
    id: "hc-addiction-treatment",
    title: "Addiction Recovery Program",
    description: "Outpatient addiction treatment and counseling program.",
    price: 25000,
    image: "/healthcare-maternity.jpg",
    duration: "6 months",
    category: "Mental Health"
  },
  {
    id: "hc-sleep-study",
    title: "Sleep Disorder Assessment",
    description: "Complete sleep study with CPAP therapy if needed.",
    price: 8500,
    image: "/healthcare-maternity.jpg",
    duration: "Overnight study",
    category: "Sleep Medicine"
  }
];

export default function Healthcare() {
  const [reportListing, setReportListing] = useState<string | null>(null);
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="pt-20">
        {/* Hero Section */}
        <section 
          className="relative py-24 px-4 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroHealthcareImg})` }}
        >
          <div className="absolute inset-0 bg-[hsl(var(--flexaura-primary-dark)/0.85)]"></div>
          <div className="relative container mx-auto text-center text-white">
            <h1 className="text-4xl md:text-6xl font-heading font-semibold mb-6">Flex Healthcare</h1>
            <SandboxBadge />
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-95 font-body">
              Compare procedure estimates and choose a financing plan (BNPL, bank/finance, or a mix) tailored to your bill.
            </p>
          </div>
        </section>

      <DataFreshnessNotice vertical="healthcare" />

      <main className="container mx-auto px-4 py-12">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src={pkg.image} 
                  alt={pkg.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <FlexauraVerifiedBadge
                    tier={DEFAULT_VERIFICATION.tier}
                    verifiedDate={DEFAULT_VERIFICATION.verifiedDate}
                    verifiedProvider={DEFAULT_VERIFICATION.verifiedProvider}
                  />
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                    {pkg.category}
                  </span>
                  <span>•</span>
                  <span>{pkg.duration}</span>
                  {pkg.facility && (
                    <>
                      <span>•</span>
                      <span>{pkg.facility}</span>
                    </>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{pkg.title}</h3>
                <p className="text-muted-foreground text-sm mb-2 line-clamp-2">{pkg.description}</p>
                <p className="text-[11px] font-mono text-[hsl(var(--flexaura-accent))] mb-2">
                  Earn ~{Math.floor(pkg.price * 0.01 * 10).toLocaleString()} pts on this booking
                </p>
                <VerificationDisclaimer
                  verifiedProvider={DEFAULT_VERIFICATION.verifiedProvider}
                  verifiedDate={DEFAULT_VERIFICATION.verifiedDate}
                />
                <div className="flex items-center justify-between mt-2">
                  <div className="text-2xl font-bold font-mono text-foreground">
                    AED {pkg.price.toLocaleString()}
                  </div>
                  <Button asChild>
                    <Link to={`/healthcare/treatment/${pkg.id}`}>
                      Check Availability
                    </Link>
                  </Button>
                </div>
                <div className="mt-2">
                  <ProviderLogoPills />
                </div>
                <button
                  onClick={() => setReportListing(pkg.title)}
                  className="mt-2 text-[10px] text-muted-foreground hover:underline text-left"
                >
                  Listing outdated? Let us know
                </button>
              </div>
            </Card>
          ))}
        </div>
        </main>
        <CompareAllBar listingId="healthcare" />
        <ReportListingModal
          listingName={reportListing ?? ""}
          vertical="Healthcare"
          open={!!reportListing}
          onOpenChange={(o) => !o && setReportListing(null)}
        />
      </div>
    </div>
  );
}

export { packages as HEALTHCARE_PACKAGES };